import type { APIGatewayProxyEventV2WithRequestContext, Context } from 'aws-lambda'
import type { PrincipalScope } from '../rbac/scope.js'

export interface CustomAuthorizerContext {
	user?: {
		userId: string
		email: string
		role: UserRole
		providerId?: string
		/** External IdP subject (Cognito `sub`), present on OIDC-minted tokens. */
		externalId?: string
		/** Tenant context for RLS propagation (Epic-02 Story 6). */
		tenantId?: string
		/** Real RBAC role names (e.g. `ORG_MANAGER`); OIDC-minted tokens. */
		roles?: string[]
		/** Expanded permission atoms (e.g. `customer.read`); OIDC-minted tokens. */
		permissions?: string[]
		/** Hierarchy scope resolved with the principal (TO-388); absent means no unit. */
		scope?: PrincipalScope
	}
}

export type CustomAPIGatewayProxyEventV2 = APIGatewayProxyEventV2WithRequestContext<{
	authorizer: CustomAuthorizerContext
}> & {
	/**
	 * The request body exactly as it arrived, captured by the middleware before
	 * JSON parsing replaces `body` with the parsed value.
	 *
	 * Only routes that must reason about the literal bytes need this — today, the
	 * inbound webhook receiver, which verifies an HMAC signature the sender
	 * computed over them. Parsing is not lossless (key order, duplicate keys and
	 * number formatting all change), so re-serialising `body` would produce a
	 * different byte sequence and therefore a different digest.
	 *
	 * Still base64 when `isBase64Encoded` is set — read it through
	 * `getRawBodyBuffer` rather than treating it as text.
	 */
	rawBody?: string
}

export type ApiHandler = (event: CustomAPIGatewayProxyEventV2, context: Context) => Promise<ApiResponse>

export interface ApiResponse {
	statusCode: number
	body: string
	headers?: Record<string, string>
	/** API Gateway HTTP API (v2) Set-Cookie values; emitted as Set-Cookie headers. */
	cookies?: string[]
}

export type ApiRoutesMap = {
	GET?: Record<string, ApiHandler>
	POST?: Record<string, ApiHandler>
	PUT?: Record<string, ApiHandler>
	PATCH?: Record<string, ApiHandler>
	DELETE?: Record<string, ApiHandler>
	OPTIONS?: Record<string, ApiHandler>
}

/**
 * Coarse authority level carried on the single `role` field of the auth context
 * and consumed by the frontend viewer model. One level per RBAC `Role` so the
 * mapping is lossless — `ORG_MANAGER`, `ORG_ADMIN` and `PLATFORM_ADMIN` are each
 * distinguishable, and only a real SUPER_ADMIN reads as super admin.
 *
 * `ADMIN` is the generic "an admin of some kind" floor that `isAdmin()` tests
 * against; it is the ordinal of the lowest admin role (ORG_MANAGER), so any
 * admin-or-above satisfies it. It is not itself an RBAC role name.
 *
 * Ordering is authority-ascending and lives in `ROLE_ORDINAL` (role-helpers.ts
 * on the backend, role-order.ts on the frontend); keep all three in step.
 */
export enum UserRole {
	SUPER_ADMIN = 'SUPER_ADMIN',
	PLATFORM_ADMIN = 'PLATFORM_ADMIN',
	ORG_ADMIN = 'ORG_ADMIN',
	ORG_MANAGER = 'ORG_MANAGER',
	/** Generic admin floor — equal to ORG_MANAGER's ordinal. */
	ADMIN = 'ADMIN',
	USER = 'USER',
	VIEWER = 'VIEWER',
}

export interface QueryParams {
	page?: number
	limit?: number
	sort?: string
	order?: 'asc' | 'desc'
	filter?: Record<string, string | string[]>
	expand?: string[]
	fields?: string[]
}

export interface QueryConfig {
	ALLOWED_FIELDS: Set<string>
	ALLOWED_FILTERS: Set<string>
	ALLOWED_EXPANDS: Set<string>
	ALLOWED_SORT_FIELDS: Set<string>
	DEFAULT_PAGE: number
	DEFAULT_LIMIT: number
	MAX_LIMIT: number
}

export type CustomHandler<TEvent = CustomAPIGatewayProxyEventV2, TResult = ApiResponse> = (
	event: TEvent,
	context: Context,
) => Promise<TResult>
